import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { SecurityStateService } from './security-state.service';
import { SecurityEventsService } from './security-events.service';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { RecognizedHotkey } from './types/recognized-hotkey.type';

@Injectable({
  providedIn: 'root',
})
export class DisableHotkeysService implements OnDestroy {
  private initialized = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT)
    private readonly document: Document,
    private readonly securityStateService: SecurityStateService,
    private readonly securityEventsService: SecurityEventsService,
  ) {}

  async initialize(): Promise<void> {
    const alreadyInitialized = this.initialized;
    const isEnabled = await this.securityStateService.isEnabled('pass-hotkey');

    if (alreadyInitialized || !isEnabled) {
      return;
    } else {
      this.initialized = true;
    }

    this.run();
  }

  private run(): void {
    const keydownHotkeys$ = fromEvent<KeyboardEvent>(this.document, 'keydown', {
      capture: true,
    }).pipe(
      map(
        (event) =>
          <RecognizedHotkey>{
            event,
            action: this.recognizeAction(event),
          },
      ),
    );

    const keyupHotkeys$ = fromEvent<KeyboardEvent>(this.document, 'keyup', { capture: true }).pipe(
      filter((event) => this.isPrintScreen(event)),
      map(
        (event) =>
          <RecognizedHotkey>{
            event,
            action: 'screenshot',
          },
      ),
    );

    merge(keydownHotkeys$, keyupHotkeys$)
      .pipe(
        filter(({ action }) => action !== 'neutral'),
        takeUntil(this.destroy$),
      )
      .subscribe(({ event, action }) => {
        this.sendEvent(action);
        this.block(event);
      });

    fromEvent<KeyboardEvent>(this.document, 'keyup', { capture: true })
      .pipe(
        filter((event) => this.isPrintScreen(event)),
        map((event) => ({
          event,
          action: 'screenshot',
        })),
        takeUntil(this.destroy$),
      )
      .subscribe(({ event, action }) => {
        this.sendEvent(action);
        this.block(event);
      });
  }

  private sendEvent(action: string): void {
    this.securityEventsService.sendEvent('pass-hotkey', {
      action,
    });
  }

  private recognizeAction(
    event: KeyboardEvent,
  ): 'save-page' | 'print-page' | 'screenshot' | 'neutral' {
    const { code, ctrlKey, metaKey, shiftKey } = event;
    const isCtrlOrMeta = ctrlKey || metaKey;

    if (isCtrlOrMeta && !shiftKey) {
      if (code === 'KeyS') {
        return 'save-page';
      }

      if (code === 'KeyP') {
        return 'print-page';
      }
    }

    if (this.isPrintScreen(event)) {
      return 'screenshot';
    }

    // Windows Snipping Tool: Win + Shift + S
    if (this.isWindows() && metaKey && shiftKey && code === 'KeyS') {
      return 'screenshot';
    }

    // macOS Screenshot: Cmd + Shift + 3 / 4 / 5
    if (this.isMac() && metaKey && shiftKey && ['Digit3', 'Digit4', 'Digit5'].includes(code)) {
      return 'screenshot';
    }

    return 'neutral';
  }

  private isPrintScreen(event: KeyboardEvent): boolean {
    return event.code === 'PrintScreen' || event.key === 'PrintScreen';
  }

  private block(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  private isWindows(): boolean {
    return /windows|win32|win64/i.test(this.getPlatform());
  }

  private isMac(): boolean {
    return /mac|macos|macintel/i.test(this.getPlatform());
  }

  private getPlatform(): string {
    const nav = navigator as Navigator & {
      userAgentData?: {
        platform?: string;
      };
    };

    return nav.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
